import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { VscNewFile, VscRefresh } from 'react-icons/vsc';
import { connect } from 'react-redux';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as privilege from '../../../ressources/glossaires/glossaire_privileges';
import * as requestType from '../../../ressources/glossaires/glossaire_requetes';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import * as actions from '../../../services/actions';
import Bouton from '../../UI/Bouton/Bouton';
import DataGrid from '../../UI/DataGrid/DataGrid';
import Toolbar from '../../UI/Toolbar/Toolbar';
import AccesRestreins from '../../../hoc/AccesRestreins/AccesRestreins';
import styles from './TableauUtilisateur.module.scss';

const COLUMN_CONFIG = [
  ['user_id', 'id', 120],
  ['name', 'prenom', 160],
  ['lastname', 'nom', 160],
  ['email', 'email', 200],
  ['company_name', 'entreprise', 180],
  ['phone_index', 'index3', 120],
  ['phone_number', 'telephone', 160],
  ['active', 'etat_de_compte', 160],
  ['privileges_list', 'privilege', 200],
  ['projects_list_id', 'projet_liste', 200],
];

const createColumn = (field, labelKey, width) => ({
  field,
  headerName: checkMot(labelKey),
  width,
  sortable: true,
  align: 'center',
  headerAlign: 'center',
});

const TableauUtilisateur = (props) => {
  const { recupererListeUtilisateurs, recupererListePrivilegesParCategorie } =
    props;

  useEffect(() => {
    // Chargement initial : liste des utilisateurs et privilèges par catégorie.
    // On dépend des seules fonctions de dispatch (références stables fournies
    // par connect) et non de `props`, qui change à chaque rendu : la réponse
    // des requêtes met à jour le store, ce qui re-rendait le composant et
    // relançait l'effet en boucle.
    recupererListeUtilisateurs();
    recupererListePrivilegesParCategorie();
  }, [recupererListeUtilisateurs, recupererListePrivilegesParCategorie]);

  const [selectionModel, setSelectionModel] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const columns = useMemo(
    () =>
      COLUMN_CONFIG.map(([field, labelKey, width]) =>
        createColumn(field, labelKey, width)
      ),
    []
  );

  // Handle row selection
  const handleRowSelection = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      if (newSelection.length > 0) {
        const row = (props.listeUtilisateurs || []).find(
          (item) => item.user_id === newSelection[0]
        );
        if (row) {
          props.selectioneLigne(row);
        }
      }
    },
    [props.listeUtilisateurs, props.selectioneLigne]
  );

  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  const validRows = useMemo(
    () =>
      (props.listeUtilisateurs || []).filter(
        (row) => row.user_id !== null && row.user_id !== undefined
      ),
    [props.listeUtilisateurs]
  );

  const filteredRows = useMemo(() => {
    if (!searchValue) return validRows;
    const needle = searchValue.toLowerCase();
    return validRows.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(needle)
      )
    );
  }, [validRows, searchValue]);

  return (
    <div className={styles.Tableau}>
      <Toolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        searchPlaceholder="recherche"
      >
        <Bouton
          hoverText={checkMot('refresh')}
          type={type_btn.outil}
          rond={true}
          cliquer={props.recupererListeUtilisateurs}
        >
          <VscRefresh size={15} />
        </Bouton>
        <AccesRestreins
          listNumPrivilege={[
            privilege.creer_des_clients,
            privilege.creer_des_visiteurs,
            privilege.creer_des_supers_admins,
            privilege.creer_des_admins,
          ]}
        >
          <Bouton
            hoverText={checkMot('creer')}
            type={type_btn.outil}
            rond={true}
            cliquer={props.creer}
          >
            <VscNewFile size={15} />
          </Bouton>
        </AccesRestreins>
      </Toolbar>
      <div className={styles.TableauBootstrap}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.user_id}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelection}
          disableMultipleRowSelection
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    listeUtilisateurs: state.utilisateur.utilisateursListe,
    listeProjets: state.authentifie.projects_list,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    recupererListeUtilisateurs: () =>
      dispatch(actions.reqRecupererListeUtilisateurs()),
    recupererListePrivilegesParCategorie: () =>
      dispatch(actions.reqListePrivilegesParCategorie()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TableauUtilisateur);
